/**
 * Hook for broadcast/announcement operations (Phase 39).
 *
 * Plain data hook (useState + useCallback) — NOT a Context provider.
 * Mirrors useCannedResponses exactly: mutations call api → on success re-fetch
 * list → return MutationResult.
 *
 * Exposes:
 *   - broadcasts list + loadBroadcasts() (called on mount by the screen)
 *   - previewAudience(filter) → count (no refetch — read-only preview)
 *   - createBroadcast(input) → MutationResult & { id? } (re-fetches list)
 *   - cancelBroadcast(id) → MutationResult (re-fetches list)
 *   - retryFailed(id) → MutationResult (re-fetches list)
 *   - sendTest(chatId, message) → MutationResult (no refetch — not a broadcast)
 *   - loadCategoryTags() → string[] (D-03: DISTINCT tags from tickets)
 *   - getBroadcast(id) → BroadcastDetail | null (for detail view)
 */

import { useCallback, useState } from 'react';
import { apiClient } from '../services/api';
import type {
  Broadcast,
  BroadcastDetail,
  AudienceFilter,
  CreateBroadcastInput,
  UploadSummary,
} from '../types';

type MutationResult = { ok: true } | { ok: false; error: string };
type CreateResult = { ok: true; id: string } | { ok: false; error: string };

export function useAnnouncements() {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Load the broadcast list ─────────────────────────────────────────────
  const loadBroadcasts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiClient.listBroadcasts();
      if (result.success && result.data) {
        setBroadcasts(Array.isArray(result.data) ? result.data : []);
      } else {
        setError(result.error || 'Failed to load broadcasts');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load broadcasts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Internal re-fetch used by mutations
  const refreshBroadcasts = useCallback(async () => {
    const result = await apiClient.listBroadcasts();
    if (result.success && result.data) {
      setBroadcasts(Array.isArray(result.data) ? result.data : []);
    }
  }, []);

  // ── Preview audience count (no refetch — read-only) ────────────────────
  const previewAudience = useCallback(
    async (filter: AudienceFilter): Promise<{ ok: true; count: number } | { ok: false; error: string }> => {
      const result = await apiClient.previewBroadcastAudience(filter);
      if (!result.success) return { ok: false, error: result.error || 'Failed to preview audience' };
      return { ok: true, count: result.data?.count ?? 0 };
    },
    [],
  );

  // ── Create a broadcast (D-04 typed-count guard happens server-side) ────
  const createBroadcast = useCallback(
    async (input: CreateBroadcastInput): Promise<CreateResult> => {
      const result = await apiClient.createBroadcast(input);
      if (!result.success) {
        return { ok: false, error: result.error || 'Failed to create broadcast' };
      }
      await refreshBroadcasts();
      return { ok: true, id: result.data?.id ?? '' };
    },
    [refreshBroadcasts],
  );

  // ── Cancel a broadcast (re-fetch so status updates in list) ───────────
  const cancelBroadcast = useCallback(
    async (id: string): Promise<MutationResult> => {
      const result = await apiClient.cancelBroadcast(id);
      if (!result.success) return { ok: false, error: result.error || 'Failed to cancel broadcast' };
      await refreshBroadcasts();
      return { ok: true };
    },
    [refreshBroadcasts],
  );

  // ── Retry failed recipients (re-fetch list after requeue) ─────────────
  const retryFailed = useCallback(
    async (id: string): Promise<MutationResult> => {
      const result = await apiClient.retryFailedBroadcast(id);
      if (!result.success) return { ok: false, error: result.error || 'Failed to retry failed recipients' };
      await refreshBroadcasts();
      return { ok: true };
    },
    [refreshBroadcasts],
  );

  // ── Send test (D-02 — no broadcastRecipientId, no broadcast mutation) ─
  const sendTest = useCallback(
    async (
      chatId: number | string,
      message: string,
      opts?: { title?: string; linkUrl?: string; linkLabel?: string },
    ): Promise<MutationResult> => {
      const result = await apiClient.testSendBroadcast(chatId, message, opts);
      if (!result.success) return { ok: false, error: result.error || 'Failed to send test message' };
      return { ok: true };
    },
    [],
  );

  // ── Load category tags (D-03 — DISTINCT tags from tickets) ────────────
  const loadCategoryTags = useCallback(async (): Promise<string[]> => {
    const result = await apiClient.listBroadcastCategoryTags();
    if (result.success && result.data?.tags) {
      return result.data.tags;
    }
    return [];
  }, []);

  // ── Get a single broadcast (for detail view) ──────────────────────────
  const getBroadcast = useCallback(async (id: string): Promise<BroadcastDetail | null> => {
    const result = await apiClient.getBroadcast(id);
    if (result.success && result.data) {
      return result.data;
    }
    return null;
  }, []);

  // ── Phase 40: SV sub-discovery (data-driven — never hardcode the list) ─
  const loadSimVerifierSubs = useCallback(async (): Promise<string[]> => {
    const result = await apiClient.listSimVerifierSubs();
    if (result.success && result.data?.subs) {
      return result.data.subs;
    }
    return [];
  }, []);

  // ── Phase 40: CSV upload dry-run (returns the matched/invalid summary) ─
  const uploadRecipients = useCallback(
    async (
      file: File,
    ): Promise<{ ok: true; summary: UploadSummary } | { ok: false; error: string }> => {
      const result = await apiClient.uploadRecipients(file);
      if (!result.success || !result.data) {
        return { ok: false, error: result.error || 'Failed to upload recipients' };
      }
      return { ok: true, summary: result.data };
    },
    [],
  );

  // ── Phase 40: create a broadcast from an uploaded CSV (re-fetch list) ──
  const createFromUpload = useCallback(
    async (
      fields: { title: string; message: string; link_url?: string | null; link_label?: string | null },
      file: File,
    ): Promise<CreateResult> => {
      const result = await apiClient.createBroadcastFromUpload(fields, file);
      if (!result.success || !result.data) {
        return { ok: false, error: result.error || 'Failed to create broadcast from upload' };
      }
      await refreshBroadcasts();
      return { ok: true, id: result.data.id };
    },
    [refreshBroadcasts],
  );

  // ── Phase 40: mint the invalid-recipients download URL (D-03) ─────────
  const getInvalidLink = useCallback(async (id: string): Promise<string | null> => {
    const result = await apiClient.getBroadcastInvalidLink(id);
    if (result.success && result.data?.url) {
      return result.data.url;
    }
    return null;
  }, []);

  return {
    broadcasts,
    isLoading,
    error,
    loadBroadcasts,
    refreshBroadcasts,
    previewAudience,
    createBroadcast,
    cancelBroadcast,
    retryFailed,
    sendTest,
    loadCategoryTags,
    getBroadcast,
    loadSimVerifierSubs,
    uploadRecipients,
    createFromUpload,
    getInvalidLink,
  };
}
